/**
 * Environment Service - Infrastructure Layer
 *
 * Provides centralized, type-safe access to environment configuration.
 * Reads from window.__env (populated by src/assets/env.js) and provides defaults.
 *
 * Architecture:
 * - Infrastructure layer service (like httpClient)
 * - Read once at initialization
 * - Provides type-safe access with defaults
 * - Can be used directly or via useEnv hook
 *
 * Usage:
 *   import { envService } from '../services/env.service';
 *   const logoUrl = envService.logoUrl;
 */

import type { EnvironmentConfig, PartialEnvironmentConfig } from '../types/env';

/**
 * Default environment configuration
 * Provides fallback values if window.__env is not available or incomplete
 */
const DEFAULT_ENV_CONFIG: EnvironmentConfig = {
  // Core URLs
  apiUrl: 'http://localhost:8080/finpro_admin/v1/',
  logoUrl: 'assets/images/universal-bank.png',
  staticBaseUrl: 'http://localhost:4200/',
  downloadurl: './documentation/',

  // Application Config
  fiuId: 'LendingKart',
  productId: 'CARLOAN',
  pageSize: 50,
  supportEmail: 'support[at]moneyone[dot]in',
  tokenFromUI: '',

  // Feature Flags
  newUI: true,
  dynamicDomainURL: false,
  paylodEncKey: false,
  redirection: true,
  redirectionNewTab: false,
  downloadjson: true,
  bankStatementView: true,
  idbiSchema: false,
  internalMetrics: true,
  fipBoard: false,
  endUserList: true,
  faqUrl: false,

  // Complex Configs
  ssoLogin: {
    ssoLoginButton: false,
  },
  bulkUsers: {
    bulkUsersButton: true,
  },
  termsAndConditions: {
    pathUrl: 'https://www.onemoney.in/tandc.html',
    showButton: 'true',
    displayText: 'Terms & Services (Terms & Conditions)',
  },
  apmStatus: {
    apmText: 'APM Code: APP04754',
    apmView: false,
  },
  ui_config: {
    common: {
      data_analytics: false,
      pdf_analytics: false,
      rbihAccess: false,
      download_account_wise_xml: false,
      show_download_button_ews: false,
      show_download_button_bulk_fetch: false,
    },
    multi_authentication: false,
    userConfig: {
      fipBoard: true,
      bulkUsers: {
        bulkUsersButton: true,
      },
      consentList: {
        dataDownload: true,
        bankStatementView: true,
        downloadCSVButton: true,
        consentRequestButton: true,
        consentFilterByUserId: true,
      },
      endUserList: true,
      internalMetrics: true,
    },
  },

  // VWO
  vwoAccountId: '1075449',

  // Forgot Password - Old password placeholder (used when backend doesn't validate oldpassword)
  forgotPasswordPlaceholder: '',
};

/**
 * Environment Service Class
 * Singleton that reads window.__env and merges with defaults
 */
class EnvService {
  private config: EnvironmentConfig;

  constructor() {
    this.config = this.loadEnvironmentConfig();
  }

  /**
   * Load environment configuration from window.__env
   * Merges with defaults to ensure all properties are available
   */
  private loadEnvironmentConfig(): EnvironmentConfig {
    const browserWindow = (typeof window !== 'undefined' ? window : {}) as Window;
    const browserWindowEnv: PartialEnvironmentConfig = browserWindow.__env || {};

    // Deep merge browserWindowEnv with DEFAULT_ENV_CONFIG
    // For simple properties, browserWindowEnv takes precedence
    // For nested objects, we merge recursively
    const config: EnvironmentConfig = {
      ...DEFAULT_ENV_CONFIG,
      ...browserWindowEnv,
      // Merge nested objects explicitly
      ssoLogin: {
        ...DEFAULT_ENV_CONFIG.ssoLogin,
        ...(browserWindowEnv.ssoLogin || {}),
      },
      bulkUsers: {
        ...DEFAULT_ENV_CONFIG.bulkUsers,
        ...(browserWindowEnv.bulkUsers || {}),
      },
      termsAndConditions: {
        ...DEFAULT_ENV_CONFIG.termsAndConditions,
        ...(browserWindowEnv.termsAndConditions || {}),
      },
      apmStatus: {
        ...DEFAULT_ENV_CONFIG.apmStatus,
        ...(browserWindowEnv.apmStatus || {}),
      },
      ui_config: {
        common: {
          ...DEFAULT_ENV_CONFIG.ui_config.common,
          ...(browserWindowEnv.ui_config?.common || {}),
        },
        multi_authentication:
          browserWindowEnv.ui_config?.multi_authentication ??
          DEFAULT_ENV_CONFIG.ui_config.multi_authentication,
        userConfig: {
          ...DEFAULT_ENV_CONFIG.ui_config.userConfig,
          ...(browserWindowEnv.ui_config?.userConfig || {}),
          bulkUsers: {
            ...DEFAULT_ENV_CONFIG.ui_config.userConfig.bulkUsers,
            ...(browserWindowEnv.ui_config?.userConfig?.bulkUsers || {}),
          },
          consentList: {
            ...DEFAULT_ENV_CONFIG.ui_config.userConfig.consentList,
            ...(browserWindowEnv.ui_config?.userConfig?.consentList || {}),
          },
        },
      },
    };

    return config;
  }

  /**
   * Get the complete environment configuration
   */
  public getConfig(): EnvironmentConfig {
    return this.config;
  }

  /**
   * Convenience getters for commonly used values
   */
  public get apiUrl(): string {
    return this.config.apiUrl;
  }

  public get logoUrl(): string {
    return this.config.logoUrl;
  }

  public get fiuId(): string {
    return this.config.fiuId;
  }

  public get pageSize(): number {
    return this.config.pageSize;
  }

  public get downloadUrl(): string {
    return this.config.downloadurl;
  }

  public get staticBaseUrl(): string {
    return this.config.staticBaseUrl;
  }

  public get supportEmail(): string {
    return this.config.supportEmail;
  }

  public get isNewUI(): boolean {
    return this.config.newUI;
  }

  public get isMultiAuthentication(): boolean {
    return this.config.ui_config.multi_authentication;
  }

  public get isFaqEnabled(): boolean {
    return this.config.faqUrl;
  }

  public get vwoAccountId(): string {
    return this.config.vwoAccountId;
  }

  public get forgotPasswordPlaceholder(): string {
    return this.config.forgotPasswordPlaceholder;
  }

  /**
   * Check if a feature is enabled in ui_config
   */
  public isFeatureEnabled(feature: keyof EnvironmentConfig): boolean {
    const value = this.config[feature];
    return typeof value === 'boolean' ? value : false;
  }

  /**
   * Get UI config for specific section
   */
  public getUIConfig() {
    return this.config.ui_config;
  }

  /**
   * Get terms and conditions config
   */
  public getTermsAndConditions() {
    return this.config.termsAndConditions;
  }

  /**
   * Get APM status config
   */
  public getAPMStatus() {
    return this.config.apmStatus;
  }

  /**
   * Reload configuration from window.__env
   * Useful if environment is updated dynamically (rare case)
   */
  public reload(): void {
    this.config = this.loadEnvironmentConfig();
  }
}

/**
 * Singleton instance
 * Export this for direct usage in services or non-component code
 */
export const envService = new EnvService();

/**
 * Export class for testing purposes
 */
export { EnvService };
